import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { TagIcon, CircleStackIcon, LinkIcon, Squares2X2Icon, MusicalNoteIcon, InformationCircleIcon, XMarkIcon, ChevronDownIcon } from '@heroicons/react/24/outline';
import TagGraph from '../components/TagGraph';
import BookmarkGraph from '../components/BookmarkGraph';
import BipartiteGraph from '../components/BipartiteGraph';
import ClusterGraph from '../components/ClusterGraph';
import ChordDiagram from '../components/ChordDiagram';
import { bookmarkService } from '../services/bookmarkService';
import { useDarkMode } from '../hooks/useDarkMode';

const GRAPH_DESCRIPTIONS = {
  tags:      'Shows how often tags appear together on the same bookmarks. Larger nodes are more-used tags; thicker edges mean more bookmarks share that tag pair. Use this to find which topics in your library are closely related.',
  bookmarks: 'Connects bookmarks that share tags. Edge thickness reflects how many tags two bookmarks have in common. Adjust the minimum shared-tags slider to reduce noise and surface only the strongest relationships.',
  bipartite: 'Shows both bookmarks and tags as separate node types connected by edges. Click a tag to see all bookmarks carrying it, or click a bookmark to see its tags.',
  cluster:   'Groups bookmarks by their primary tag using shaded regions. Bookmarks are connected when they share enough tags (controlled by the slider). Hover over a shaded region to identify the cluster. Use this to spot bookmarks that bridge multiple topic areas.',
  chord:     'Summarises tag co-occurrence across all bookmarks. Each arc segment represents a tag; the ribbons between them represent bookmarks that carry both tags — thicker ribbons mean more shared bookmarks. Hover an arc or ribbon for exact counts; click an arc to filter bookmarks by that tag.',
};

const DATE_RANGES = [
  { label: 'All time', value: 'all' },
  { label: '3 months', value: '3m' },
  { label: '30 days', value: '30d' },
  { label: '7 days', value: '7d' },
];

function getDateThreshold(range) {
  if (range === 'all') return null;
  const now = new Date();
  if (range === '7d')  { now.setDate(now.getDate() - 7); return now; }
  if (range === '30d') { now.setDate(now.getDate() - 30); return now; }
  if (range === '3m')  { now.setMonth(now.getMonth() - 3); return now; }
  return null;
}

// Searchable multi-select tag picker
const TagPicker = ({ allTags, selected, onChange }) => {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const ref = useRef(null);

  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const filtered = allTags
    .filter(t => t.toLowerCase().includes(search.toLowerCase()))
    .slice(0, 50);

  const toggle = (tag) => {
    onChange(selected.includes(tag) ? selected.filter(t => t !== tag) : [...selected, tag]);
  };

  return (
    <div ref={ref} className="relative">
      <button
        onClick={() => setOpen(o => !o)}
        className="flex items-center gap-1.5 text-sm border border-gray-200 dark:border-gray-600 rounded-md px-3 py-1.5 bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-200 hover:border-primary-400 focus:outline-none focus:ring-2 focus:ring-primary-300 min-w-[130px] justify-between"
      >
        <span className="truncate">
          {selected.length === 0 ? 'Filter by tag' : selected.length === 1 ? selected[0] : `${selected.length} tags`}
        </span>
        <ChevronDownIcon className="h-3.5 w-3.5 flex-shrink-0 text-gray-400" />
      </button>

      {open && (
        <div className="absolute z-20 mt-1 w-56 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-600 rounded-lg shadow-lg">
          <div className="p-2 border-b border-gray-100 dark:border-gray-700">
            <input
              autoFocus
              type="text"
              placeholder="Search tags…"
              value={search}
              onChange={e => setSearch(e.target.value)}
              className="w-full text-sm px-2 py-1 border border-gray-200 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none focus:ring-1 focus:ring-primary-400"
            />
          </div>
          <ul className="max-h-52 overflow-y-auto py-1">
            {filtered.length === 0 && (
              <li className="px-3 py-2 text-sm text-gray-400 dark:text-gray-500">No tags found</li>
            )}
            {filtered.map(tag => (
              <li key={tag}>
                <button
                  onClick={() => toggle(tag)}
                  className={`w-full text-left px-3 py-1.5 text-sm flex items-center gap-2 transition-colors ${
                    selected.includes(tag)
                      ? 'bg-primary-50 dark:bg-primary-900/30 text-primary-700 dark:text-primary-300'
                      : 'text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700'
                  }`}
                >
                  <span className={`h-3.5 w-3.5 rounded border flex-shrink-0 flex items-center justify-center ${
                    selected.includes(tag)
                      ? 'bg-primary-600 border-primary-600'
                      : 'border-gray-300 dark:border-gray-500'
                  }`}>
                    {selected.includes(tag) && (
                      <svg className="h-2.5 w-2.5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                      </svg>
                    )}
                  </span>
                  <span className="truncate">{tag}</span>
                </button>
              </li>
            ))}
          </ul>
          {selected.length > 0 && (
            <div className="border-t border-gray-100 dark:border-gray-700 p-2">
              <button
                onClick={() => { onChange([]); setOpen(false); }}
                className="w-full text-xs text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 py-1 text-center"
              >
                Clear all
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

const Visualization = () => {
  const navigate = useNavigate();
  const [isDark] = useDarkMode();
  const [graphMode, setGraphMode] = useState(
    () => localStorage.getItem('vizGraphMode') || 'tags'
  );
  const [scopeId, setScopeId] = useState('all');

  // Filters
  const [selectedTags, setSelectedTags] = useState([]);
  const [tagMode, setTagMode] = useState('any'); // 'any' | 'all'
  const [selectedCategory, setSelectedCategory] = useState('');
  const [dateRange, setDateRange] = useState('all');

  const [tags, setTags] = useState([]);
  const [allBookmarks, setAllBookmarks] = useState(null);
  const [collections, setCollections] = useState([]);
  const [collectionBookmarks, setCollectionBookmarks] = useState(null);
  const [loadingInitial, setLoadingInitial] = useState(true);
  const [loadingCollection, setLoadingCollection] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    async function loadVisualizationData() {
      const all = [];
      let nextToken = null;
      do {
        const response = await bookmarkService.getBookmarks({ limit: 100, nextToken });
        all.push(...(response.items || []));
        nextToken = response.nextToken || null;
      } while (nextToken && all.length < 500);
      return all;
    }

    Promise.all([
      bookmarkService.getTags({ limit: 200 }),
      loadVisualizationData(),
      bookmarkService.listCollections(),
    ])
      .then(([{ items: tagsData }, bookmarksData, { items: collectionsData }]) => {
        setTags(tagsData);
        setAllBookmarks(bookmarksData);
        setCollections(collectionsData);
      })
      .catch(() => setError('Failed to load data. Please refresh.'))
      .finally(() => setLoadingInitial(false));
  }, []);

  useEffect(() => {
    if (scopeId === 'all') {
      setCollectionBookmarks(null);
      return;
    }
    setLoadingCollection(true);
    bookmarkService.getCollection(scopeId)
      .then(data => setCollectionBookmarks(data.bookmarks || []))
      .catch(() => setCollectionBookmarks([]))
      .finally(() => setLoadingCollection(false));
  }, [scopeId]);

  const handleModeChange = (mode) => {
    setGraphMode(mode);
    localStorage.setItem('vizGraphMode', mode);
  };

  const handleNavigateToTag = useCallback((tagName) => {
    navigate('/bookmarks', { state: { filterTag: tagName } });
  }, [navigate]);

  // Base scope (all or collection)
  const scopeBookmarks = scopeId === 'all' ? (allBookmarks || []) : (collectionBookmarks || []);

  // Apply filters
  const filteredBookmarks = scopeBookmarks.filter(bm => {
    if (selectedTags.length > 0) {
      const bmTags = bm.tags || [];
      const match = tagMode === 'all'
        ? selectedTags.every(t => bmTags.includes(t))
        : selectedTags.some(t => bmTags.includes(t));
      if (!match) return false;
    }
    if (selectedCategory) {
      const bmCats = bm.categories || [];
      if (!bmCats.includes(selectedCategory)) return false;
    }
    if (dateRange !== 'all') {
      const threshold = getDateThreshold(dateRange);
      if (threshold && new Date(bm.createdAt) < threshold) return false;
    }
    return true;
  });

  // All unique categories across scope bookmarks (for the dropdown)
  const availableCategories = [...new Set(
    scopeBookmarks.flatMap(bm => bm.categories || [])
  )].sort();

  // All unique tag names across scope bookmarks (for the tag picker)
  const availableTagNames = [...new Set(
    scopeBookmarks.flatMap(bm => bm.tags || [])
  )].sort();

  const hasActiveFilters = selectedTags.length > 0 || selectedCategory || dateRange !== 'all';

  const clearFilters = () => {
    setSelectedTags([]);
    setTagMode('any');
    setSelectedCategory('');
    setDateRange('all');
  };

  const scopeLabel = scopeId !== 'all'
    ? collections.find(c => c.collectionId === scopeId)?.name
    : null;

  const countLabel = hasActiveFilters
    ? `${filteredBookmarks.length} of ${scopeBookmarks.length} bookmark${scopeBookmarks.length !== 1 ? 's' : ''}`
    : scopeLabel
      ? `${scopeBookmarks.length} bookmark${scopeBookmarks.length !== 1 ? 's' : ''}`
      : `${allBookmarks?.length ?? 0} bookmarks · ${tags.length} tag${tags.length !== 1 ? 's' : ''}`;

  if (loadingInitial) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600" />
      </div>
    );
  }

  return (
    <div>
      <div className="space-y-4">
        {/* Header row */}
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 dark:text-gray-100">Visualize</h1>
            <p className="text-gray-600 dark:text-gray-400 mt-1">{countLabel}</p>
          </div>

          <div className="flex flex-wrap items-center gap-3">
            {/* Collection scope */}
            {collections.length > 0 && (
              <select
                value={scopeId}
                onChange={e => { setScopeId(e.target.value); setSelectedTags([]); setSelectedCategory(''); }}
                className="text-sm border border-gray-200 dark:border-gray-600 rounded-md px-3 py-1.5 text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-primary-300"
              >
                <option value="all">All Bookmarks</option>
                {collections.map(c => (
                  <option key={c.collectionId} value={c.collectionId}>{c.name}</option>
                ))}
              </select>
            )}

            {/* Graph mode tabs */}
            <div className="flex items-center border border-gray-200 dark:border-gray-700 rounded-md overflow-x-auto bg-white dark:bg-gray-800">
              <button
                onClick={() => handleModeChange('tags')}
                title="Tag co-occurrence graph"
                className={`flex items-center gap-1.5 px-3 py-1.5 text-sm whitespace-nowrap transition-colors ${
                  graphMode === 'tags' ? 'bg-primary-600 text-white' : 'text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700'
                }`}
              >
                <TagIcon className="h-4 w-4 flex-shrink-0" />
                <span className="hidden sm:inline">Tags</span>
              </button>
              <button
                onClick={() => handleModeChange('bookmarks')}
                title="Bookmark similarity graph"
                className={`flex items-center gap-1.5 px-3 py-1.5 text-sm whitespace-nowrap transition-colors ${
                  graphMode === 'bookmarks' ? 'bg-primary-600 text-white' : 'text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700'
                }`}
              >
                <CircleStackIcon className="h-4 w-4 flex-shrink-0" />
                <span className="hidden sm:inline">Bookmarks</span>
              </button>
              <button
                onClick={() => handleModeChange('bipartite')}
                title="Tag + bookmark mixed graph"
                className={`flex items-center gap-1.5 px-3 py-1.5 text-sm whitespace-nowrap transition-colors ${
                  graphMode === 'bipartite' ? 'bg-primary-600 text-white' : 'text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700'
                }`}
              >
                <LinkIcon className="h-4 w-4 flex-shrink-0" />
                <span className="hidden sm:inline">Bipartite</span>
              </button>
              <button
                onClick={() => handleModeChange('cluster')}
                title="Cluster graph"
                className={`flex items-center gap-1.5 px-3 py-1.5 text-sm whitespace-nowrap transition-colors ${
                  graphMode === 'cluster' ? 'bg-primary-600 text-white' : 'text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700'
                }`}
              >
                <Squares2X2Icon className="h-4 w-4 flex-shrink-0" />
                <span className="hidden sm:inline">Cluster</span>
              </button>
              <button
                onClick={() => handleModeChange('chord')}
                title="Chord diagram"
                className={`flex items-center gap-1.5 px-3 py-1.5 text-sm whitespace-nowrap transition-colors ${
                  graphMode === 'chord' ? 'bg-primary-600 text-white' : 'text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700'
                }`}
              >
                <MusicalNoteIcon className="h-4 w-4 flex-shrink-0" />
                <span className="hidden sm:inline">Chord</span>
              </button>
            </div>
          </div>
        </div>

        {/* Filter row */}
        <div className="flex flex-wrap items-center gap-3">
          {/* Tag filter */}
          <TagPicker
            allTags={availableTagNames}
            selected={selectedTags}
            onChange={setSelectedTags}
          />

          {/* Any / All toggle — only shown when tags are selected */}
          {selectedTags.length > 1 && (
            <div className="flex items-center border border-gray-200 dark:border-gray-600 rounded-md overflow-hidden bg-white dark:bg-gray-700">
              <button
                onClick={() => setTagMode('any')}
                className={`px-2.5 py-1.5 text-xs font-medium whitespace-nowrap transition-colors ${
                  tagMode === 'any'
                    ? 'bg-primary-600 text-white'
                    : 'text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-600'
                }`}
              >
                Any
              </button>
              <button
                onClick={() => setTagMode('all')}
                className={`px-2.5 py-1.5 text-xs font-medium whitespace-nowrap transition-colors ${
                  tagMode === 'all'
                    ? 'bg-primary-600 text-white'
                    : 'text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-600'
                }`}
              >
                All
              </button>
            </div>
          )}

          {/* Category filter */}
          {availableCategories.length > 0 && (
            <select
              value={selectedCategory}
              onChange={e => setSelectedCategory(e.target.value)}
              className="text-sm border border-gray-200 dark:border-gray-600 rounded-md px-3 py-1.5 text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-primary-300"
            >
              <option value="">All categories</option>
              {availableCategories.map(cat => (
                <option key={cat} value={cat}>{cat}</option>
              ))}
            </select>
          )}

          {/* Date range */}
          <div className="flex items-center border border-gray-200 dark:border-gray-600 rounded-md overflow-hidden bg-white dark:bg-gray-700">
            {DATE_RANGES.map(({ label, value }) => (
              <button
                key={value}
                onClick={() => setDateRange(value)}
                className={`px-3 py-1.5 text-sm whitespace-nowrap transition-colors ${
                  dateRange === value
                    ? 'bg-primary-600 text-white'
                    : 'text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-600'
                }`}
              >
                {label}
              </button>
            ))}
          </div>

          {/* Clear filters */}
          {hasActiveFilters && (
            <button
              onClick={clearFilters}
              className="flex items-center gap-1 text-sm text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 transition-colors"
            >
              <XMarkIcon className="h-4 w-4" />
              Clear filters
            </button>
          )}

          {/* Active tag chips */}
          {selectedTags.map(tag => (
            <span
              key={tag}
              className="inline-flex items-center gap-1 pl-2.5 pr-1.5 py-1 bg-primary-50 dark:bg-primary-900/30 border border-primary-200 dark:border-primary-700 text-primary-700 dark:text-primary-300 text-xs rounded-full"
            >
              {tag}
              <button onClick={() => setSelectedTags(selectedTags.filter(t => t !== tag))} className="hover:text-primary-900 dark:hover:text-primary-100">
                <XMarkIcon className="h-3 w-3" />
              </button>
            </span>
          ))}
        </div>

        {/* Graph description */}
        <div className="flex items-start gap-2 bg-primary-50 dark:bg-primary-900/20 border border-primary-100 dark:border-primary-800 rounded-lg px-4 py-2.5 text-sm text-primary-800 dark:text-primary-300">
          <InformationCircleIcon className="h-4 w-4 flex-shrink-0 mt-0.5 text-primary-400" />
          <span>{GRAPH_DESCRIPTIONS[graphMode]}</span>
        </div>

        {error && (
          <div className="bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 text-red-700 dark:text-red-400 rounded-lg px-4 py-3 text-sm">
            {error}
          </div>
        )}

        {/* Graph area */}
        {loadingCollection ? (
          <div className="flex justify-center items-center h-64">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600" />
          </div>
        ) : filteredBookmarks.length === 0 && hasActiveFilters ? (
          <div className="flex flex-col items-center justify-center h-64 text-gray-400 dark:text-gray-500">
            <p className="text-base font-medium mb-1">No bookmarks match the current filters</p>
            <button onClick={clearFilters} className="text-sm text-primary-600 dark:text-primary-400 hover:underline">
              Clear filters
            </button>
          </div>
        ) : graphMode === 'tags' ? (
          <TagGraph
            tags={tags}
            bookmarks={filteredBookmarks}
            onNavigate={handleNavigateToTag}
            isDark={isDark}
          />
        ) : graphMode === 'bookmarks' ? (
          <BookmarkGraph bookmarks={filteredBookmarks} isDark={isDark} />
        ) : graphMode === 'bipartite' ? (
          <BipartiteGraph bookmarks={filteredBookmarks} onNavigate={handleNavigateToTag} isDark={isDark} />
        ) : graphMode === 'cluster' ? (
          <ClusterGraph bookmarks={filteredBookmarks} isDark={isDark} />
        ) : (
          <ChordDiagram bookmarks={filteredBookmarks} onNavigate={handleNavigateToTag} isDark={isDark} />
        )}
      </div>
    </div>
  );
};

export default Visualization;
